import { DataOutputStream, DataInputStream, FileSystem } from './java/DataStream';

/**
 * Daily Challenge — one seeded run per day, 28-day cycle (4 weeks).
 *
 * Seed: deterministic from epoch day. Same seed = same enemy spawns.
 * Difficulty rotates across the week: Mon/Tue=Normal, Wed/Thu=Hard, Fri/Sat=Insane, Sun=Hard.
 * One attempt per day. Score + streak tracked.
 */
export class DailyChallenge {

  private static readonly FILE = 'dotch_daily.dat';
  private static readonly VERSION = 1;
  private static readonly CYCLE_LENGTH = 28;
  private static readonly BASE_SEED: bigint = 0xD07CDA17n;

  // Difficulty per day-of-week (0=Mon ... 6=Sun)
  // Mon=Normal, Tue=Normal, Wed=Hard, Thu=Hard, Fri=Insane, Sat=Insane, Sun=Hard
  private static readonly DAY_DIFFICULTY: number[] = [0, 0, 1, 1, 2, 2, 1];

  // Persistent state
  private static scores: number[] = new Array<number>(DailyChallenge.CYCLE_LENGTH).fill(0);
  private static completed: boolean[] = new Array<boolean>(DailyChallenge.CYCLE_LENGTH).fill(false);
  private static lastPlayedEpochDay: bigint = 0n;
  private static currentStreak = 0;
  private static bestStreak = 0;
  private static totalCompleted = 0;
  private static bestDailyScore = 0;

  static init(): void {
    DailyChallenge.load();
  }

  // ===== Day / Cycle calculations =====

  /** Current epoch day (days since 1970-01-01) */
  static todayEpochDay(): bigint {
    const now = new Date();
    const utcMidnight = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    return BigInt(Math.floor(utcMidnight / 86400000));
  }

  /** Position in the 28-day cycle (0-27) */
  static dayInCycle(): number {
    return Number(DailyChallenge.todayEpochDay() % BigInt(DailyChallenge.CYCLE_LENGTH));
  }

  /** Week within the cycle (0-3) */
  static weekInCycle(): number {
    return Math.trunc(DailyChallenge.dayInCycle() / 7);
  }

  /** Day of week (0=Mon ... 6=Sun) */
  static dayOfWeek(): number {
    return DailyChallenge.dayInCycle() % 7;
  }

  /** Seed for today's challenge */
  static todaySeed(): bigint {
    const epochDay = DailyChallenge.todayEpochDay();
    const cycle = BigInt(DailyChallenge.CYCLE_LENGTH);
    const cycleDay = epochDay % cycle;
    const week = cycleDay / 7n;
    const dayInWeek = cycleDay % 7n;
    // Same day-of-week across weeks shares a base pattern, week adds variation
    const mix = dayInWeek * 7919n + week * 104729n + (epochDay / cycle) * 999983n;
    return BigInt.asIntN(64, DailyChallenge.BASE_SEED ^ BigInt.asUintN(64, mix));
  }

  /** Difficulty for today */
  static todayDifficulty(): number {
    return DailyChallenge.DAY_DIFFICULTY[DailyChallenge.dayOfWeek()];
  }

  static todayDifficultyName(): string {
    return DailyChallenge.todayDifficulty() === 0 ? 'Normal'
      : DailyChallenge.todayDifficulty() === 1 ? 'Hard' : 'Insane';
  }

  /** Human-readable day label */
  static dayLabel(dayInCycle: number): string {
    const weekdays: string[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const week = Math.trunc(dayInCycle / 7);
    const dow = dayInCycle % 7;
    return 'W' + (week + 1) + ' ' + weekdays[dow];
  }

  static todayLabel(): string {
    return DailyChallenge.dayLabel(DailyChallenge.dayInCycle());
  }

  // ===== Attempt tracking =====

  static hasPlayedToday(): boolean {
    return DailyChallenge.lastPlayedEpochDay === DailyChallenge.todayEpochDay();
  }

  static canPlay(): boolean {
    return !DailyChallenge.hasPlayedToday();
  }

  /** Submit today's score. Called once when the daily run ends. */
  static submitScore(score: number): void {
    const today = DailyChallenge.todayEpochDay();
    const day = DailyChallenge.dayInCycle();

    DailyChallenge.scores[day] = score;
    DailyChallenge.completed[day] = true;
    DailyChallenge.totalCompleted++;
    if (score > DailyChallenge.bestDailyScore) DailyChallenge.bestDailyScore = score;

    // Streak — check if yesterday was played
    if (DailyChallenge.lastPlayedEpochDay === today - 1n) {
      DailyChallenge.currentStreak++;
    } else if (DailyChallenge.lastPlayedEpochDay !== today) {
      DailyChallenge.currentStreak = 1;
    }
    if (DailyChallenge.currentStreak > DailyChallenge.bestStreak) {
      DailyChallenge.bestStreak = DailyChallenge.currentStreak;
    }

    DailyChallenge.lastPlayedEpochDay = today;
    DailyChallenge.save();
  }

  // ===== Getters =====

  static getScore(dayInCycle: number): number {
    return (dayInCycle >= 0 && dayInCycle < DailyChallenge.CYCLE_LENGTH)
      ? DailyChallenge.scores[dayInCycle] : 0;
  }

  static isCompleted(dayInCycle: number): boolean {
    return (dayInCycle >= 0 && dayInCycle < DailyChallenge.CYCLE_LENGTH)
      && DailyChallenge.completed[dayInCycle];
  }

  static getCurrentStreak(): number { return DailyChallenge.currentStreak; }
  static getBestStreak(): number { return DailyChallenge.bestStreak; }
  static getTotalCompleted(): number { return DailyChallenge.totalCompleted; }
  static getBestDailyScore(): number { return DailyChallenge.bestDailyScore; }

  static getDifficultyForDay(dayInCycle: number): number {
    return DailyChallenge.DAY_DIFFICULTY[dayInCycle % 7];
  }

  static getDifficultyNameForDay(dayInCycle: number): string {
    const d = DailyChallenge.getDifficultyForDay(dayInCycle);
    return d === 0 ? 'Normal' : d === 1 ? 'Hard' : 'Insane';
  }

  // ===== Cycle reset check =====

  /** Called on load — if we've moved to a new cycle, clear old scores */
  private static checkCycleReset(): void {
    // If more than CYCLE_LENGTH days since last play, reset the board
    const daysSince = DailyChallenge.todayEpochDay() - DailyChallenge.lastPlayedEpochDay;
    if (daysSince > BigInt(DailyChallenge.CYCLE_LENGTH)) {
      DailyChallenge.scores = new Array<number>(DailyChallenge.CYCLE_LENGTH).fill(0);
      DailyChallenge.completed = new Array<boolean>(DailyChallenge.CYCLE_LENGTH).fill(false);
      DailyChallenge.currentStreak = 0;
      DailyChallenge.save();
    }
  }

  // ===== Persistence =====

  private static save(): void {
    try {
      const out = new DataOutputStream(DailyChallenge.FILE);
      out.writeInt(DailyChallenge.VERSION);
      out.writeLong(DailyChallenge.lastPlayedEpochDay);
      out.writeInt(DailyChallenge.currentStreak);
      out.writeInt(DailyChallenge.bestStreak);
      out.writeInt(DailyChallenge.totalCompleted);
      out.writeInt(DailyChallenge.bestDailyScore);
      for (let i = 0; i < DailyChallenge.CYCLE_LENGTH; i++) {
        out.writeInt(DailyChallenge.scores[i]);
        out.writeBoolean(DailyChallenge.completed[i]);
      }
      out.close();
    } catch {
      // Silent
    }
  }

  private static load(): void {
    const in_: DataInputStream | null = FileSystem.open(DailyChallenge.FILE);
    if (in_ === null) return;
    try {
      const version = in_.readInt();
      if (version >= 1) {
        DailyChallenge.lastPlayedEpochDay = in_.readLong();
        DailyChallenge.currentStreak = in_.readInt();
        DailyChallenge.bestStreak = in_.readInt();
        DailyChallenge.totalCompleted = in_.readInt();
        DailyChallenge.bestDailyScore = in_.readInt();
        for (let i = 0; i < DailyChallenge.CYCLE_LENGTH; i++) {
          DailyChallenge.scores[i] = in_.readInt();
          DailyChallenge.completed[i] = in_.readBoolean();
        }
      }
      in_.close();
    } catch {
      // Corrupted
    }
    DailyChallenge.checkCycleReset();
  }

  static resetAll(): void {
    DailyChallenge.scores = new Array<number>(DailyChallenge.CYCLE_LENGTH).fill(0);
    DailyChallenge.completed = new Array<boolean>(DailyChallenge.CYCLE_LENGTH).fill(false);
    DailyChallenge.lastPlayedEpochDay = 0n;
    DailyChallenge.currentStreak = 0;
    DailyChallenge.bestStreak = 0;
    DailyChallenge.totalCompleted = 0;
    DailyChallenge.bestDailyScore = 0;
    DailyChallenge.save();
  }
}
